import React, {
    useEffect
} from 'react';

import PostsTableComponent from '../PostsTableComponent.jsx';
import SingleGlanceMetricDisplay from '../SingleGlanceMetricDisplay.jsx';
import AssociatedTerms from './AssociatedTerms.jsx';
import WizardScrollingPanel from './WizardScrollingPanel.jsx';

import {
    LoadingStatus,
    useSetInstanceLoadingStatus,
    useGetInstanceLoadingStatuses
} from '../../../../hooks/loadingStatusHooks.js';

import {
    Loading
} from '../../../../components/MetricLoadingView.jsx';

import {
    useVerifySearch,
    ValidationStates
} from '../../hooks/useVerifySearch.js';

import useListeningCurationFilterValues from '../../hooks/useListeningCurationFilterValues.js';
import useListeningSearchTimePeriod from '../../hooks/useListeningSearchTimePeriod.js';

import useMetrics from '../../../../hooks/useMetrics.js';

import DelayedWidgetLoad from './DelayedWidgetLoad.jsx';

import {
    listeningDefinitions
} from '../enums/listeningDefinitions.jsx';

const {
    previewTopicTitle
} = listeningDefinitions['saved-search'];

function PreviewGlanceWidget({instanceId}) {
    const metricFilters = useListeningCurationFilterValues(instanceId);
    const interval = 'day';

    const metricRequests = {
        count: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [],
            measure: 'count',
            filters: metricFilters
        },

        countByDay: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [interval],
            measure: 'count',
            filters: metricFilters
        }
    };

    const {isLoading, data: metricResults} = useMetrics(metricRequests);

    const statusUpdater = useSetInstanceLoadingStatus(instanceId);
    useEffect(() => {
        statusUpdater(isLoading ? LoadingStatus.LOADING : LoadingStatus.COMPLETE);
    }, [isLoading, statusUpdater]);

    return (
        <div
            className='tlw-p-7.5'
        >
            {metricResults &&
                <SingleGlanceMetricDisplay
                    metricRequest={metricRequests.count}
                    metricResult={metricResults.count}
                    metricName='Posts'
                    renderSparkLine={true}
                    metricResultForSparkLine={metricResults.countByDay}
                />
            }
        </div>
    );

}

const getTimePeriodDisplayString = (timePeriod) => {
    if (timePeriod.id !== RivalIQ.enums.timePeriod.type.CUSTOM) {
        return timePeriod.displayString;
    }
    return RivalIQ.utils.timePeriod.getMainPeriodDisplayString(timePeriod);
};

export default function SavedSearchPreview({instanceId, isSampleTopic, isSourceInvalid}) {

    const {validationState, orderedErrors} = useVerifySearch(true);
    const {effective: timePeriod} = useListeningSearchTimePeriod(isSampleTopic, false);

    const hasFatalErrors = _.find(orderedErrors, {
        isFatal: true
    });

    const showPreview = validationState !== ValidationStates.Invalid && !hasFatalErrors && !isSourceInvalid;

    const glanceInstanceId = `${instanceId}-glance`;
    const wordCloudInstanceId = `${instanceId}-word-cloud`;
    const postsInstanceId = `${instanceId}-posts`;

    const widgetLoadStatus = useGetInstanceLoadingStatuses([glanceInstanceId, wordCloudInstanceId, postsInstanceId]);
    const showLoading = _.some(widgetLoadStatus, status => status === LoadingStatus.LOADING);

    const metricFilters = useListeningCurationFilterValues(instanceId);
    const metricRequests = {};
    if (showPreview) {
        metricRequests.count = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [],
            measure: 'count',
            filters: metricFilters
        };
    }
    const {data: metricResults} = useMetrics(metricRequests, {
        requestGroup: 'verifySearch'
    });
    const hasNBQApiError = metricResults?.count?.error?.name === 'NetbaseApiError';

    return (
        <div
            className='tlw-flex tlw-w-full tlw-flex-col tlw-rounded tlw-bg-white tlw-shadow-panel lg_tlw-absolute'
        >
            <div
                className='tlw-m-0 tlw-flex tlw-grow tlw-flex-row tlw-px-7.5 tlw-py-5 tlw-text-base tlw-leading-22 tlw-text-gray-900'
            >
                <div
                    className='tlw-overflow-hidden tlw-text-ellipsis tlw-whitespace-nowrap'
                >
                    <h4 className='tlw-m-0 tlw-text-lg tlw-leading-base tlw-text-gray-900'>{previewTopicTitle}</h4>
                    <div className='tlw-text-md tlw-leading-20'>{getTimePeriodDisplayString(timePeriod)}</div>
                </div>

            </div>
            <WizardScrollingPanel
                className='tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300'
            >
                {showPreview && !hasNBQApiError &&
                <>
                    <DelayedWidgetLoad
                        instanceId={glanceInstanceId}
                    >
                        <PreviewGlanceWidget />
                    </DelayedWidgetLoad>

                    <DelayedWidgetLoad
                        instanceId={wordCloudInstanceId}
                    >
                        <AssociatedTerms
                        />
                    </DelayedWidgetLoad>

                    <DelayedWidgetLoad
                        instanceId={postsInstanceId}
                        className='tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300'
                    >
                        <PostsTableComponent
                            instanceId={postsInstanceId}
                            showOnlyPostsColumn={true}
                            limit={10}
                        />
                    </DelayedWidgetLoad>

                    {showLoading &&
                    <div>
                        <Loading />
                    </div>
                    }
                </>
                }

                {hasNBQApiError &&
                <div
                    className='tlw-p-7.5'>
                    We are sorry, but we cannot perform this search. Please

                    {window.Intercom &&
                    <>
                        <button
                            className='tlw-border-0 tlw-bg-transparent tlw-p-0 tlw-text-blue-400'
                            onClick={() => window.Intercom('show')}
                        >
                             &nbsp;contact us&nbsp;
                        </button>
                    </>
                    }
                    {!window.Intercom &&
                    <>
                        &nbsp;
                        <a
                            href='mailto:support@rivaliq.com'>
                        contact us
                        </a>
                        &nbsp;
                    </>
                    }
                    and we will investigate the issue.
                </div>
                }

            </WizardScrollingPanel>
        </div>
    );
}
